/**
 * QuestDock - Global quest panel above the tab bar
 *
 * A static dock that sits between the main content area and the bottom tab
 * navigation. Collapsed: header bar only. Expanded: scrollable list showing
 * ~4 quest tiles with auto-offer cards and new quest notifications.
 */

import { useState, useMemo, useEffect } from 'react';
import { useGameStore, useActiveQuests, usePendingQuestOffer, useNewlyUnlockedQuests } from '@/state';
import { marketManager, questManager, questBoardManager } from '@/systems';
import { eventBus } from '@/services/EventBus';
import { GameEvent, ActiveQuest, QuestDefinition } from '@/types';
import { getQuestNavigationTarget } from '@/utils';
import { QuestTile } from './QuestTile';
import { QuestAutoOfferCard } from './QuestAutoOfferCard';
import { NewQuestNotification } from './NewQuestNotification';

const MAX_VISIBLE_QUESTS = 4;

/** Sort priority by category (lower = higher in list) */
const CATEGORY_SORT_ORDER: Record<string, number> = {
  tutorial: 0,       // isTutorial quests get mapped here
  progression: 1,
  buddy_quest: 2,
  daily: 3,
  challenge: 4,
  bounty: 5,
};
const DEFAULT_CATEGORY_ORDER = 6;

/** Categories that show a consolidated "check quest board" notification instead of individual cards */
const BOARD_NOTIFICATION_CATEGORIES = new Set(['buddy_quest', 'daily', 'challenge', 'bounty']);

function getCategorySortOrder(def: QuestDefinition): number {
  if (def.isTutorial) return CATEGORY_SORT_ORDER.tutorial;
  return CATEGORY_SORT_ORDER[def.category ?? ''] ?? DEFAULT_CATEGORY_ORDER;
}

export function QuestDock() {
  const [expanded, setExpanded] = useState(() => {
    const saved = localStorage.getItem('questDock:expanded');
    return saved === 'true';
  });

  const activeQuests = useActiveQuests();
  const pendingOffer = usePendingQuestOffer();
  const newlyUnlocked = useNewlyUnlockedQuests();

  // Split newly unlocked quests into:
  // - "important" (tutorial/progression) → individual accept cards
  // - "board" (buddy/daily/challenge/bounty) → consolidated notification
  const { importantUnlocked, boardUnlockedIds } = useMemo(() => {
    let filtered = newlyUnlocked;
    if (pendingOffer) {
      filtered = filtered.filter((n) => n.questId !== pendingOffer.nextQuest.id);
    }

    const important: typeof filtered = [];
    const boardIds: string[] = [];

    for (const n of filtered) {
      const def = marketManager.getQuestDefinition(n.questId);
      if (def && BOARD_NOTIFICATION_CATEGORIES.has(def.category ?? '')) {
        boardIds.push(n.questId);
      } else {
        important.push(n);
      }
    }

    return { importantUnlocked: important, boardUnlockedIds: boardIds };
  }, [newlyUnlocked, pendingOffer]);

  // Track procedural/buddy quest refills ready on the quest board
  const [hasRefillsReady, setHasRefillsReady] = useState(false);

  useEffect(() => {
    const check = () => {
      setHasRefillsReady(questBoardManager.hasAnyRefillsReady());
    };

    // Check immediately
    check();

    // Re-check after a short delay to catch initialization race
    // (loadCache() runs in App useEffect AFTER child effects)
    // Also checks for cached unaccepted quests to guide returning players
    const initTimeout = setTimeout(() => {
      const hasRefills = questBoardManager.hasAnyRefillsReady();
      if (hasRefills) {
        setHasRefillsReady(true);
        return;
      }
      // Check for cached quests from a previous session (returning player)
      const state = useGameStore.getState();
      const activeIds = new Set(state.playerState.quests.activeQuests.map((q) => q.questId));
      const completedIds = new Set(state.playerState.quests.completedQuests);
      setHasRefillsReady(questBoardManager.hasUnacceptedCachedQuests(activeIds, completedIds));
    }, 500);

    // Schedule a precise timeout for when the next refill is due
    let refillTimeout: ReturnType<typeof setTimeout> | null = null;
    const scheduleNextRefill = () => {
      if (refillTimeout) clearTimeout(refillTimeout);
      const nextTime = questBoardManager.getNextRefillTime();
      if (nextTime !== null) {
        const delay = Math.max(100, nextTime - Date.now() + 100);
        refillTimeout = setTimeout(() => {
          check();
          scheduleNextRefill();
        }, delay);
      }
    };
    scheduleNextRefill();

    // Re-check when a quest is accepted (changes refill schedule)
    const unsubAccepted = eventBus.on(GameEvent.QUEST_ACCEPTED, () => {
      setTimeout(() => {
        check();
        scheduleNextRefill();
      }, 100);
    });

    // Fallback poll (longer interval since we have precise scheduling)
    const interval = setInterval(() => {
      check();
      scheduleNextRefill();
    }, 60_000);

    return () => {
      clearTimeout(initTimeout);
      if (refillTimeout) clearTimeout(refillTimeout);
      unsubAccepted();
      clearInterval(interval);
    };
  }, []);

  // Combined flag: show "new quests" notification if either curated board quests
  // were unlocked OR procedural refills are ready
  const showBoardNotification = boardUnlockedIds.length > 0 || hasRefillsReady;

  // Resolve quest definitions and sort (completed first)
  const questData = useMemo(() => {
    return activeQuests
      .map((aq) => {
        const def = marketManager.getQuestDefinition(aq.questId);
        if (!def) return null;
        return { activeQuest: aq, definition: def };
      })
      .filter((d): d is { activeQuest: ActiveQuest; definition: QuestDefinition } => d !== null)
      .sort((a, b) => {
        // Completed quests first (ready to turn in)
        const aComplete = a.activeQuest.completed ? 0 : 1;
        const bComplete = b.activeQuest.completed ? 0 : 1;
        if (aComplete !== bComplete) return aComplete - bComplete;

        // Then by category priority
        const catDiff = getCategorySortOrder(a.definition) - getCategorySortOrder(b.definition);
        if (catDiff !== 0) return catDiff;

        // Stable tiebreaker: quest ID (alphabetical for curated, chronological for procedural)
        return a.activeQuest.questId.localeCompare(b.activeQuest.questId);
      });
  }, [activeQuests]);

  // Get completed quest name for auto-offer display
  const completedQuestName = useMemo(() => {
    if (!pendingOffer) return '';
    const def = marketManager.getQuestDefinition(pendingOffer.completedQuestId);
    return def?.name ?? pendingOffer.completedQuestId;
  }, [pendingOffer]);

  // Persist expanded state
  useEffect(() => {
    localStorage.setItem('questDock:expanded', String(expanded));
  }, [expanded]);

  // Auto-expand on important quest events
  useEffect(() => {
    const unsubCompleted = eventBus.on(GameEvent.QUEST_COMPLETED, () => {
      setExpanded(true);
    });
    // Note: QUEST_ACCEPTED intentionally does NOT auto-expand — the widget
    // popping open after every accept is disruptive to gameplay flow.
    const unsubNewUnlocked = eventBus.on(GameEvent.NEW_QUEST_UNLOCKED, () => {
      setExpanded(true);
    });
    const unsubNextAvailable = eventBus.on(GameEvent.NEXT_QUEST_AVAILABLE, () => {
      setExpanded(true);
    });
    return () => {
      unsubCompleted();
      unsubNewUnlocked();
      unsubNextAvailable();
    };
  }, []);

  // Navigation handler
  const handleNavigate = (data: { activeQuest: ActiveQuest; definition: QuestDefinition }) => {
    const target = getQuestNavigationTarget(data.definition, data.activeQuest);
    if (!target) return;

    useGameStore.setState((draft) => {
      draft.currentTab = target.tab;
      if (target.questId && target.tab === 'markets') {
        // Find the market that has this quest for sell panel routing
        const markets = marketManager.getAllMarkets();
        const market = markets.find((m) =>
          m.questBoard.curatedQuestPool.includes(target.questId!)
        );
        if (market) {
          draft.targetMarketId = market.id;
        }
        draft.activeQuestSell = target.questId;
      }
    });

    // Collapse dock after navigating
    setExpanded(false);
  };

  const handleTurnIn = (questId: string) => {
    const def = marketManager.getQuestDefinition(questId);
    if (!def) return;
    questManager.claimRewards(questId, def);
  };

  const handleAcceptOffer = () => {
    questManager.acceptPendingQuestOffer();
  };

  const handleDismissOffer = () => {
    useGameStore.getState().clearPendingQuestOffer();
  };

  const handleNavigateToQuestBoard = () => {
    useGameStore.setState((draft) => {
      draft.currentTab = 'markets';
    });

    // Dismiss all board-category unlock notifications
    for (const questId of boardUnlockedIds) {
      questManager.dismissQuestNotification(questId);
    }

    // Clear refill flag (quests will be generated when quest board renders)
    setHasRefillsReady(false);

    setExpanded(false);
  };

  const handleAcceptUnlocked = (questId: string) => {
    const def = marketManager.getQuestDefinition(questId);
    if (!def) return;
    questManager.acceptQuest(def);
    useGameStore.getState().dismissNewlyUnlockedQuest(questId);
  };

  const handleDismissNotification = (questId: string) => {
    questManager.dismissQuestNotification(questId);
  };

  const handleAbandon = (questId: string) => {
    questManager.abandonQuest(questId);
  };

  // Don't render if nothing to show
  const hasContent = questData.length > 0 || pendingOffer || importantUnlocked.length > 0 || showBoardNotification;
  if (!hasContent) return null;

  const totalCount = questData.length;
  const completedCount = questData.filter((q) => q.activeQuest.completed).length;

  return (
    <div className="shrink-0 border-t border-gray-700/50 bg-gray-900/95">
      {/* Header bar — always visible */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-800/50 transition-colors"
        aria-label={expanded ? 'Collapse quest dock' : 'Expand quest dock'}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <span className="text-base">📋</span>
          <span className="text-xs font-bold text-gray-300">
            Active Quests
            {totalCount > 0 && (
              <span className="text-gray-500 font-normal ml-1">({totalCount})</span>
            )}
          </span>
          {completedCount > 0 && (
            <span className="text-[10px] bg-green-600 text-white px-1.5 py-0.5 rounded-full animate-pulse">
              {completedCount} done
            </span>
          )}
          {(importantUnlocked.length > 0 || showBoardNotification || pendingOffer) && (
            <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full animate-pulse">
              {importantUnlocked.length > 0 || showBoardNotification ? 'new' : 'offer'}
            </span>
          )}
        </div>
        <span className="text-gray-500 text-xs">
          {expanded ? '▼' : '▲'}
        </span>
      </button>

      {expanded && (
        /* ═══ EXPANDED: Scrollable quest list capped at ~4 tiles ═══ */
        <div
          className="overflow-y-auto px-4 pb-3 space-y-2"
          style={{ maxHeight: `${MAX_VISIBLE_QUESTS * 5.5}rem` }}
        >
          {/* Board-category notification — single consolidated tile */}
          {showBoardNotification && (
            <div className="bg-blue-900/20 border border-blue-500/40 rounded-lg px-3 py-2 flex items-center gap-2">
              <span className="text-lg shrink-0">📋</span>
              <p className="text-xs text-blue-200 font-semibold flex-1 min-w-0">
                New quests available!
              </p>
              <button
                onClick={handleNavigateToQuestBoard}
                className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-2.5 py-1 rounded transition-colors cursor-pointer whitespace-nowrap"
              >
                Quest Board
              </button>
            </div>
          )}

          {/* Important quest notifications (tutorial/progression) — accept directly from dock */}
          {importantUnlocked.map((n) => {
            const def = marketManager.getQuestDefinition(n.questId);
            return (
              <NewQuestNotification
                key={n.questId}
                questId={n.questId}
                questName={n.questName}
                definition={def}
                onAccept={handleAcceptUnlocked}
                onNavigate={handleNavigateToQuestBoard}
                onDismiss={() => handleDismissNotification(n.questId)}
              />
            );
          })}

          {/* Auto-offer card */}
          {pendingOffer && (
            <QuestAutoOfferCard
              quest={pendingOffer.nextQuest}
              completedQuestName={completedQuestName}
              onAccept={handleAcceptOffer}
              onDismiss={handleDismissOffer}
            />
          )}

          {/* All quest tiles */}
          {questData.map((data) => {
            const navTarget = getQuestNavigationTarget(data.definition, data.activeQuest);
            return (
              <QuestTile
                key={data.activeQuest.questId}
                activeQuest={data.activeQuest}
                definition={data.definition}
                onTurnIn={handleTurnIn}
                onNavigate={navTarget ? () => handleNavigate(data) : undefined}
                navigateLabel={navTarget?.label}
                navigateIcon={navTarget?.icon}
                onAbandon={handleAbandon}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
